/**
 * Task Executions tab: paginated table of maintenance task runs.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, GitBranch } from 'lucide-react';
import type { Task, Pagination } from './types';
import { formatDate } from './formatting';
import { StatusBadge, Pager } from './helpers';

interface TasksTabProps {
  tasks: Task[];
  tasksLoading: boolean;
  taskPage: number;
  setTaskPage: React.Dispatch<React.SetStateAction<number>>;
  taskPagination: Pagination | null;
}

export function TasksTab({
  tasks,
  tasksLoading,
  taskPage,
  setTaskPage,
  taskPagination,
}: TasksTabProps) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
      {tasksLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : tasks.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">No maintenance tasks found.</div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <th className="px-4 py-3">Task</th>
                <th className="px-4 py-3">Repository</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">PR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {tasks.map((task) => (
                <tr
                  key={task.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/tasks/${task.id}`}
                      className="font-medium text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      {task.taskClass}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-xs">
                    {task.repository?.fullName ?? task.repository?.name ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={task.status} />
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{task.user?.email ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {formatDate(task.startedAt)}
                  </td>
                  <td className="px-4 py-3">
                    {task.prUrl ? (
                      <a
                        href={task.prUrl as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-teal-600 dark:text-teal-400 hover:underline"
                      >
                        <GitBranch className="w-3 h-3" />
                        PR
                      </a>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager
            page={taskPage}
            pagination={taskPagination}
            onPrev={() => setTaskPage((p) => Math.max(0, p - 1))}
            onNext={() => setTaskPage((p) => p + 1)}
          />
        </>
      )}
    </div>
  );
}
